import { useEffect, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import ptosService, { CreatePTOBody, UpdatePTOOptions } from '@/services/ptos';
import { PTORequest, PTOType } from '@/types/api';
import RequestTimeOffModalComponent, {
  Props as ComponentProps,
  ConfirmationModalMode,
  FormData,
} from './RequestTimeOffModal.component';

type Props = Pick<ComponentProps, 'isOpen' | 'onClose' | 'initialPTOType' | 'editing'> & {
  onTimeOffRequested?: (timeOffInstance: PTORequest) => void;
  onTimeOffUpdated?: (timeOffInstance: PTORequest) => void;
  onTimeOffDeleted?: () => void;
};

const RequestTimeOffModal = ({
  onTimeOffRequested,
  onTimeOffUpdated,
  editing,
  onTimeOffDeleted,
  ...componentProps
}: Props): JSX.Element => {
  const { profile } = useProfile();
  const createPTOMutation = useApiSubmit<PTORequest, CreatePTOBody>(ptosService.createPTORequest);
  const editPTOMutation = useApiSubmit<PTORequest, UpdatePTOOptions>(ptosService.updatePTO);
  const deletePTOMutation = useApiSubmit<null, string>(ptosService.deletePTO);
  const toast = useToastContext();
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [savedData, setSavedData] = useState<FormData>();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<ConfirmationModalMode>();
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);

  const handleSubmit = (data: FormData): void => {
    setIsConfirmationOpen(true);
    setSavedData(data);
  };

  useEffect(() => {
    if (!componentProps.isOpen) {
      setIsConfirmationOpen(false);
    }
  }, [componentProps.isOpen, setIsConfirmationOpen]);

  const handleConfirm = async (): Promise<void> => {
    if (savedData) {
      try {
        if (editing) {
          const request = await editPTOMutation.mutateAsync({
            id: editing.id,
            staffMemberId: editing.staffMemberId,
            dateRange: {
              startDate: savedData.from,
              endDate: savedData.to,
            },
            hours: Object.values(savedData.quantities || {}).map((hoursString) =>
              parseInt(hoursString, 10)
            ),
            ptoType:
              savedData.category === PTOType.PRIMARY_CAREGIVER ||
              savedData.category === PTOType.SECONDARY_CAREGIVER
                ? savedData.caregiverStatus
                : savedData.category,
            requestComment: savedData.note,
          });
          setIsSuccessModalOpen('updated');
          onTimeOffUpdated?.(request);
        } else {
          const request = await createPTOMutation.mutateAsync({
            firstDay: savedData.from,
            lastDay: savedData.to,
            hours: Object.values(savedData.quantities || {}).map((hoursString) =>
              parseInt(hoursString, 10)
            ),
            requestDate: new Date(),
            ptoType:
              savedData.category === PTOType.PRIMARY_CAREGIVER ||
              savedData.category === PTOType.SECONDARY_CAREGIVER
                ? (savedData.caregiverStatus as PTOType)
                : savedData.category,
            userSlackId: profile.slackId || undefined,
            userId: profile.id,
            userEmail: profile.email,
            requestComment: savedData.note,
          });
          setIsSuccessModalOpen('created');
          onTimeOffRequested?.(request);
        }
        componentProps.onClose?.();
      } catch (error: any) {
        toast.showErrorFeedback(
          error?.response?.data?.message || 'There was a problem trying to request the PTO.'
        );
      } finally {
        setIsConfirmationOpen(false);
      }
    }
  };

  const handleDeleteClick = (): void => {
    setIsDeleteConfirmationModalOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (editing) {
      try {
        await deletePTOMutation.mutateAsync(editing.id);
        setIsSuccessModalOpen('canceled');
        onTimeOffDeleted?.();
        componentProps.onClose?.();
      } catch (error) {
        toast.showErrorFeedback('There was a problem trying to delete the Upcoming PTO.');
      } finally {
        setIsDeleteConfirmationModalOpen(false);
      }
    }
  };

  const handleDeleteCancel = (): void => {
    setIsDeleteConfirmationModalOpen(false);
  };

  return (
    <RequestTimeOffModalComponent
      onSubmit={handleSubmit}
      isLoading={
        createPTOMutation.isLoading || editPTOMutation.isLoading || deletePTOMutation.isLoading
      }
      isConfirmationOpen={isConfirmationOpen}
      onConfirmationClose={() => setIsConfirmationOpen(false)}
      onConfirm={handleConfirm}
      isSuccessModalOpen={isSuccessModalOpen}
      onSuccessModalClose={() => setIsSuccessModalOpen(undefined)}
      isDeleteConfirmationModalOpen={isDeleteConfirmationModalOpen}
      onDeleteClick={handleDeleteClick}
      onDeleteConfirm={handleDeleteConfirm}
      onDeleteCancel={handleDeleteCancel}
      editing={editing}
      {...componentProps}
    />
  );
};

export default RequestTimeOffModal;
